/**
 * AddEmployeeModal — New Employee creation, opened from the Team Roster
 * toolbar "+ Add" button (gated on storeSettings.teamMgmt.teamRoster.edit,
 * same claim MemberProfileModal's row-click uses — no new permission claim).
 *
 * ABSOLUTE-OVERLAY SHELL (not `@pimm/base`/native-base `Modal`) — this modal
 * hosts the shared FieldSelectDialog (Role + Importer Sync pickers), which is
 * itself an absolute-overlay shell to avoid a nested-Modal focus trap (see
 * team-management-field-select-dialog.tsx's header comment). Nesting a real
 * Modal inside another real Modal reproduces that same bug, so this modal
 * stays a plain overlay too, even though it isn't nested inside anything
 * itself.
 *
 * Field requirements mirror the server validator (Sms.Workforce.Api
 * CreateTeamMemberRequest.Validator): First Name, Last Name, Employee Role
 * always required; Employee Number required only when Import Sync Mode is
 * Protected or Auto (manual creates default to Locked, matching the server's
 * own default when the field is omitted).
 *
 * First/Last/Title/Birthday/Hire Date/Email/Mobile Phone are react-hook-form
 * fields (useFormAddEmployee, same yup-resolver pattern as every other form
 * in this module). Role, Import Sync Mode, and Employee ID are staged as
 * plain component state instead — same split MemberProfileModal already uses
 * for its FieldSelectDialog-driven fields, since those values come from a
 * picker dialog, not a registered form control.
 *
 * Create is disabled until every required field is valid — there is no
 * click-to-reveal-errors gate; the disabled button IS the required-field
 * signal. onCreate returns Promise<TeamMember> and is awaited: a resolved
 * create advances to the "Employee Created" success panel; a rejected create
 * surfaces an in-modal error banner and keeps the form open for retry.
 *
 * The success panel reads the resolved record's user.userName (nested,
 * authoritative field on TeamMember) for a Manager/User create — same
 * Promote-parity 2x2 card layout as MemberProfileModal's "User Created" panel
 * (InfoCard from team-management-confirm-modal.tsx), with a Copy details
 * button. Crew (no login) shows a plain confirmation line instead.
 */
import { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Clipboard from 'expo-clipboard';
import { Box, ChevronDownIcon, HStack, Pressable, ScrollView, VStack, CheckCircleIcon, CloseIcon, WarningIcon } from 'native-base';
import { Button, ControlInput, CopyIcon, FormHelperText, InputField, RefreshCwIcon, Text, UserRoundCheckIcon } from '@pimm/base';

import { FieldSelectDialog, type FieldSelectOption } from './team-management-field-select-dialog';
import { InfoCard } from './team-management-confirm-modal';
import { importSyncIconColor, roleBadgeStyle } from './team-management-table-shared';
import { useFormAddEmployee } from '../hooks/form-add-employee.hook';
import { maskDate, maskMonthDay } from '../utils';
import type { ImportSyncMode, TeamMember, TeamMemberRole } from '../types';

export type AddEmployeeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  // Returns the created record — awaited by handleCreate; the resolved
  // record's user.userName drives the success panel, a reject drives the
  // in-modal error banner.
  onCreate: (values: AddEmployeeValues) => Promise<TeamMember>;
  roleLabels: Record<TeamMemberRole, string>;
  storeCode?: string;
  chainName: string;
};

export type AddEmployeeValues = {
  firstName: string;
  lastName: string;
  title: string;
  birthDate: string;
  hireDate: string;
  email: string;
  mobileNumber: string;
  role: TeamMemberRole;
  employeeNum: string;
  importSyncMode: ImportSyncMode;
};

const ROLE_ORDER: TeamMemberRole[] = ['SMS EMPLOYEE', 'SMS USER', 'SMS MANAGER'];
const SYNC_MODE_ORDER: ImportSyncMode[] = ['Locked', 'Protected', 'Auto'];

const grantsLoginRole = (role: TeamMemberRole | '') => role === 'SMS MANAGER' || role === 'SMS USER';

const FormField = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
  <VStack flex={1} space={0.5} minW="170px">
    <Text size="sm" fontWeight={600} color="gray.700">
      {label}
      {required ? ' *' : ''}
    </Text>
    <Box>{children}</Box>
  </VStack>
);

// Matches FormField's label style above, for the react-hook-form fields that
// render their own label via ControlInput.
const FIELD_LABEL_STYLE = { size: 'sm' as const, fontWeight: 600 as const };

// Labeled trigger that opens a FieldSelectDialog — reads like a select
// control (leading glyph + current value + chevron) but the actual choice +
// its description lives in the dialog.
const SelectTrigger = ({
  leading,
  value,
  placeholder,
  onPress,
}: {
  leading?: React.ReactNode;
  value: string;
  placeholder?: boolean;
  onPress: () => void;
}) => (
  <Pressable
    onPress={onPress}
    borderWidth={1}
    borderColor="gray.300"
    rounded="md"
    bg="white"
    px={3}
    py={2.5}
    _hover={{ borderColor: 'gray.400' }}
    accessibilityRole="button"
    accessibilityLabel={value}
  >
    <HStack alignItems="center" space={2}>
      {leading}
      <Text size="md" color={placeholder ? 'gray.400' : 'gray.900'} flex={1}>
        {value}
      </Text>
      <ChevronDownIcon size={4} color="gray.500" />
    </HStack>
  </Pressable>
);

export const AddEmployeeModal = ({ isOpen, onClose, onCreate, roleLabels, storeCode, chainName }: AddEmployeeModalProps) => {
  const { t } = useTranslation();
  const renderErrorText = (key?: string) => (key ? t(key) : undefined);

  const form = useFormAddEmployee();
  const [role, setRole] = useState<TeamMemberRole | ''>('');
  const [importSyncMode, setImportSyncMode] = useState<ImportSyncMode>('Locked');
  const [employeeNum, setEmployeeNum] = useState('');

  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [successState, setSuccessState] = useState<{ role: TeamMemberRole; userName?: string } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!errorBanner) return;
    const id = setTimeout(() => setErrorBanner(null), 5000);
    return () => clearTimeout(id);
  }, [errorBanner]);

  const employeeNumRequired = importSyncMode !== 'Locked';
  const employeeNumError =
    employeeNumRequired && !employeeNum.trim() ? t('store_settings:add_employee_employee_num_required_hint') : undefined;

  const hasErrors = !form.formState.isValid || !role || !!employeeNumError;

  const reset = () => {
    form.reset();
    setRole('');
    setImportSyncMode('Locked');
    setEmployeeNum('');
    setSuccessState(null);
    setErrorBanner(null);
    setCopied(false);
    setIsRoleDialogOpen(false);
    setIsSyncDialogOpen(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = form.handleSubmit(async values => {
    if (!role || employeeNumError) return;
    setErrorBanner(null);
    setIsSaving(true);
    try {
      const created = await onCreate({ ...values, role, employeeNum, importSyncMode });
      setSuccessState({ role, userName: created?.user?.userName });
    } catch {
      setErrorBanner(t('store_settings:add_employee_error'));
    } finally {
      setIsSaving(false);
    }
  });

  const handleDone = () => {
    reset();
    onClose();
  };

  const handleCopyDetails = async () => {
    if (!successState?.userName) return;
    const text = [
      `${t('store_settings:promote_login_info_customer_label')}: ${chainName}`,
      `${t('store_settings:username')}: ${successState.userName}`,
      `${t('store_settings:add_employee_success_password_label')}: ${storeCode ?? ''}`,
      `${t('store_settings:promote_login_info_role_label')}: ${roleLabels[successState.role]}`,
    ].join('\n');
    await Clipboard.setStringAsync(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  const syncModeLabel = (mode: ImportSyncMode) =>
    mode === 'Locked'
      ? t('store_settings:auto_sync_off')
      : mode === 'Protected'
      ? t('store_settings:auto_sync_protected')
      : t('store_settings:auto_sync_on');

  const syncModeDescription = (mode: ImportSyncMode) =>
    mode === 'Locked'
      ? t('store_settings:sync_desc_off')
      : mode === 'Protected'
      ? t('store_settings:sync_desc_protected')
      : t('store_settings:sync_desc_on');

  const roleDescription = (r: TeamMemberRole) =>
    r === 'SMS MANAGER'
      ? t('store_settings:role_desc_manager')
      : r === 'SMS USER'
      ? t('store_settings:role_desc_user')
      : t('store_settings:role_desc_employee');

  const grantsLogin = grantsLoginRole(role);
  const successGrantsLogin = successState ? grantsLoginRole(successState.role) : false;

  const roleOptions = useMemo(
    (): FieldSelectOption[] =>
      ROLE_ORDER.map(r => ({
        value: r,
        label: roleLabels[r],
        description: roleDescription(r),
        tone: r === 'SMS MANAGER' ? 'warning' : r === 'SMS USER' ? 'accent' : 'neutral',
        leading: <Box w={3} h={3} rounded="full" bg={roleBadgeStyle(r).bg} />,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roleLabels, t],
  );

  const syncOptions = useMemo(
    (): FieldSelectOption[] =>
      SYNC_MODE_ORDER.map(mode => ({
        value: mode,
        label: syncModeLabel(mode),
        description: syncModeDescription(mode),
        tone: mode === 'Auto' ? 'accent' : mode === 'Protected' ? 'warning' : 'danger',
        leading: <RefreshCwIcon size={5} color={importSyncIconColor(mode)} />,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  if (!isOpen) return null;

  return (
    <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={50}>
      {/* Backdrop dismiss is blocked while a create is in flight — a save
          shouldn't be interruptible mid-write. */}
      <Pressable
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0,0,0,0.5)"
        onPress={isSaving ? undefined : handleClose}
      />
      <Box
        position="absolute"
        top="6%"
        alignSelf="center"
        bg="white"
        rounded="xl"
        shadow={9}
        w="720px"
        maxW="92%"
        maxH="86%"
        overflow="hidden"
      >
        {successState ? (
          <VStack space={0}>
            <VStack px={6} pt={6} pb={4} space={4}>
              <HStack space={2} alignItems="center">
                <UserRoundCheckIcon size={5} color="success.600" />
                <Text size="xl" fontWeight={700} color="gray.900">
                  {t('store_settings:add_employee_success_title')}
                </Text>
              </HStack>
              {successGrantsLogin && successState.userName ? (
                <VStack space={3}>
                  <Text size="md" color="gray.700">
                    {t('store_settings:add_employee_success_with_login_body')}
                  </Text>
                  <VStack space={3}>
                    <HStack space={3}>
                      <InfoCard label={t('store_settings:promote_login_info_customer_label')}>{chainName}</InfoCard>
                      <InfoCard label={t('store_settings:username')}>{successState.userName}</InfoCard>
                    </HStack>
                    <HStack space={3}>
                      <InfoCard label={t('store_settings:add_employee_success_password_label')}>{storeCode}</InfoCard>
                      <InfoCard label={t('store_settings:promote_login_info_role_label')}>{roleLabels[successState.role]}</InfoCard>
                    </HStack>
                  </VStack>
                </VStack>
              ) : (
                <Text size="md" color="gray.700">
                  {t('store_settings:add_employee_success_no_login_body')}
                </Text>
              )}
            </VStack>
            <HStack
              space={2}
              px={6}
              pb={6}
              alignItems="center"
              justifyContent={successGrantsLogin && successState.userName ? 'space-between' : 'flex-end'}
            >
              {successGrantsLogin && successState.userName && (
                <Button alternate outline size="sm" onPress={handleCopyDetails}>
                  <HStack space={1.5} alignItems="center">
                    {copied ? <CheckCircleIcon size={4} color="success.600" /> : <CopyIcon size={4} color="gray.600" />}
                    <Text size="sm" fontWeight={600} color={copied ? 'success.700' : 'gray.700'}>
                      {copied ? t('store_settings:promote_login_info_copied_label') : t('store_settings:add_employee_copy_details_label')}
                    </Text>
                  </HStack>
                </Button>
              )}
              <Button minH={9} onPress={handleDone}>
                {t('store_settings:promote_login_info_done_label')}
              </Button>
            </HStack>
          </VStack>
        ) : (
          <VStack space={0}>
            <VStack px={6} pt={6} pb={3}>
              <Text size="xl" fontWeight={700} color="gray.900">
                {t('store_settings:add_employee_title')}
              </Text>
            </VStack>

            {errorBanner && (
              <HStack
                mx={6}
                mb={2}
                bg="error.50"
                borderLeftWidth={3}
                borderColor="error.400"
                px={4}
                py={2}
                space={2}
                alignItems="center"
                rounded="sm"
              >
                <WarningIcon size={4} color="error.600" />
                <Text size="sm" fontWeight={400} color="error.800" flex={1}>
                  {errorBanner}
                </Text>
                <Pressable onPress={() => setErrorBanner(null)} p={1} accessibilityLabel={t('store_settings:close')}>
                  <CloseIcon size={3} color="error.700" />
                </Pressable>
              </HStack>
            )}

            <ScrollView maxH="500px" px={6}>
              <VStack space={4} pb={2}>
                <HStack space={4}>
                  <Box flex={1} minW="170px">
                    <ControlInput
                      control={form.control}
                      name="firstName"
                      label={`${t('store_settings:first_name')} *`}
                      disabled={isSaving}
                      renderErrorText={renderErrorText}
                      _label={FIELD_LABEL_STYLE}
                    />
                  </Box>
                  <Box flex={1} minW="170px">
                    <ControlInput
                      control={form.control}
                      name="lastName"
                      label={`${t('store_settings:last_name')} *`}
                      disabled={isSaving}
                      renderErrorText={renderErrorText}
                      _label={FIELD_LABEL_STYLE}
                    />
                  </Box>
                </HStack>

                <FormField label={t('store_settings:role')} required>
                  <SelectTrigger
                    leading={role ? <Box w={3} h={3} rounded="full" bg={roleBadgeStyle(role).bg} /> : undefined}
                    value={role ? roleLabels[role] : t('store_settings:add_employee_role_placeholder')}
                    placeholder={!role}
                    onPress={() => setIsRoleDialogOpen(true)}
                  />
                  {grantsLogin && (
                    <Text size="xs" color="gray.600" mt={1}>
                      {t('store_settings:add_employee_login_create_note')}
                    </Text>
                  )}
                </FormField>

                <HStack space={4}>
                  <FormField label={t('store_settings:auto_sync')}>
                    <SelectTrigger
                      leading={<RefreshCwIcon size={4} color={importSyncIconColor(importSyncMode)} />}
                      value={syncModeLabel(importSyncMode)}
                      onPress={() => setIsSyncDialogOpen(true)}
                    />
                  </FormField>
                  <FormField label={t('store_settings:employee_id')} required={employeeNumRequired}>
                    <InputField value={employeeNum} onChangeText={setEmployeeNum} isInvalid={!!employeeNumError} isDisabled={isSaving} />
                    {!!employeeNumError && (
                      <FormHelperText error visible>
                        {employeeNumError}
                      </FormHelperText>
                    )}
                  </FormField>
                </HStack>

                <HStack space={4}>
                  <Box flex={1} minW="170px">
                    <ControlInput
                      control={form.control}
                      name="title"
                      label={t('store_settings:job_title')}
                      disabled={isSaving}
                      renderErrorText={renderErrorText}
                      _label={FIELD_LABEL_STYLE}
                    />
                  </Box>
                  <Controller
                    control={form.control}
                    name="birthDate"
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <FormField label={t('store_settings:birthday')}>
                        <InputField
                          value={value}
                          onBlur={onBlur}
                          onChangeText={next => onChange(maskMonthDay(next))}
                          placeholder={t('store_settings:birthday_placeholder')}
                          keyboardType="numeric"
                          isInvalid={!!error?.message}
                          isDisabled={isSaving}
                        />
                        {!!error?.message && (
                          <FormHelperText error visible>
                            {t(error.message)}
                          </FormHelperText>
                        )}
                      </FormField>
                    )}
                  />
                  <Controller
                    control={form.control}
                    name="hireDate"
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <FormField label={t('store_settings:hire_date')}>
                        <InputField
                          value={value}
                          onBlur={onBlur}
                          onChangeText={next => onChange(maskDate(next))}
                          placeholder={t('store_settings:hire_date_placeholder')}
                          keyboardType="numeric"
                          isInvalid={!!error?.message}
                          isDisabled={isSaving}
                        />
                        {!!error?.message && (
                          <FormHelperText error visible>
                            {t(error.message)}
                          </FormHelperText>
                        )}
                      </FormField>
                    )}
                  />
                </HStack>

                <HStack space={4}>
                  <Box flex={1} minW="170px">
                    <ControlInput
                      control={form.control}
                      name="email"
                      label={t('store_settings:email')}
                      disabled={isSaving}
                      renderErrorText={renderErrorText}
                      _label={FIELD_LABEL_STYLE}
                    />
                  </Box>
                  <Box flex={1} minW="170px">
                    <ControlInput
                      control={form.control}
                      name="mobileNumber"
                      label={t('store_settings:mobile_phone')}
                      disabled={isSaving}
                      renderErrorText={renderErrorText}
                      _label={FIELD_LABEL_STYLE}
                    />
                  </Box>
                </HStack>
              </VStack>
            </ScrollView>

            <HStack space={2} px={6} py={4} justifyContent="flex-end" borderTopWidth={1} borderColor="gray.200">
              <Button alternate outline bg="white" minH={9} isDisabled={isSaving} onPress={handleClose}>
                {t('store_settings:cancel')}
              </Button>
              <Button minH={9} isLoading={isSaving} isDisabled={hasErrors} onPress={handleCreate}>
                {t('store_settings:add_employee_create_label')}
              </Button>
            </HStack>
          </VStack>
        )}
      </Box>

      <FieldSelectDialog
        isOpen={isRoleDialogOpen}
        onClose={() => setIsRoleDialogOpen(false)}
        title={t('store_settings:role')}
        currentValue={role}
        showCurrentBadge={false}
        cancelLabel={t('store_settings:cancel')}
        saveLabel={t('store_settings:add_employee_select_label')}
        onSave={v => {
          setRole(v as TeamMemberRole);
          setIsRoleDialogOpen(false);
        }}
        options={roleOptions}
      />

      <FieldSelectDialog
        isOpen={isSyncDialogOpen}
        onClose={() => setIsSyncDialogOpen(false)}
        title={t('store_settings:auto_sync')}
        currentValue={importSyncMode}
        requireChange={false}
        showCurrentBadge={false}
        cancelLabel={t('store_settings:cancel')}
        saveLabel={t('store_settings:add_employee_select_label')}
        onSave={(v, id) => {
          setImportSyncMode(v as ImportSyncMode);
          if (id !== undefined) setEmployeeNum(id);
          setIsSyncDialogOpen(false);
        }}
        options={syncOptions}
        input={{
          label: t('store_settings:employee_id'),
          initialValue: employeeNum,
          caution: t('store_settings:sync_employee_id_caution'),
          required: sel => sel !== 'Locked',
          resetLabel: t('store_settings:sync_employee_id_reset_label'),
        }}
      />
    </Box>
  );
};

export default AddEmployeeModal;
